/**
 * Opt-in field-level validation errors for the shared inspection form components.
 *
 * WHY: the public Online Scheduler reports step-validation failures only through a
 * `position: fixed` snackbar. On an embedded scheduler that `fixed` resolves against the
 * *iframe's* viewport rather than the phone's, so the error is painted off-screen and never
 * seen (SHUA-4319). This mixin is the plumbing for the inline, at-the-field half of the fix;
 * the snackbar is deliberately kept as a secondary summary.
 *
 * DELIBERATELY OPT-IN: `fieldErrors` defaults to `{}`. The internal schedulers
 * (`NewInspection.vue`, `QuoteInspection.vue`) share these components and pass nothing, so
 * they render exactly as before — "unchanged" is the default rather than something tests have
 * to prove.
 *
 * Vuetify 1.5.24 emits no `aria-invalid`, `aria-describedby` or `role="alert"` of its own
 * (verified against the installed version), so both halves below exist to supply them:
 *
 *   1. `errorAttrs(key, controlId)` — spread with `v-bind`. `error-messages` drives Vuetify's
 *      own `.v-messages__message` + `.error--text`; the two aria attributes are not declared
 *      props, so they ride `$attrs` onto the real inner <input>/<textarea> (VTextField,
 *      VTextarea and VSelect all spread `$attrs` in `genInput`, VCheckbox in `genInput`).
 *   2. the `#message` scoped slot — VInput forwards it to VMessages, so the text gets its `id`,
 *      `role="alert"` and `data-testid` *inside* Vuetify's own `.v-messages__message`
 *      container, keeping the class the audit measured as absent.
 */
export default {
  props: {
    fieldErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  methods: {
    // The message for one field key, or null when that field is valid.
    fieldError(key) {
      return (this.fieldErrors || {})[key] || null
    },
    // Bindings for a Vuetify control. Empty object when the field is valid, so a component
    // that receives no errors is byte-for-byte what it was before this mixin existed.
    errorAttrs(key, controlId) {
      const message = this.fieldError(key)
      if (!message) return {}

      return {
        'error-messages': [message],
        'aria-invalid': 'true',
        'aria-describedby': this.errorId(controlId),
      }
    },
    // Derived from the control's own id so `aria-describedby` always resolves.
    errorId(controlId) {
      return `${controlId}-error`
    },
    errorTestid(key) {
      return `field-error-${key}`
    },
  },
}
